import type { CSSProperties } from 'react';
import type { Question, VisitorAnswer } from '@/domain/types';
import { Indicator } from './Indicator';
import styles from './journey.module.css';

/**
 * The four reusable answer controls.
 *
 * All of them are built on real `<input type="radio">` / `<input
 * type="checkbox">` elements inside a `<fieldset>`, hidden visually but not
 * from assistive technology. That buys correct roving-arrow-key behaviour,
 * correct grouping semantics and correct announcement for free, which a
 * div-with-role reimplementation almost never gets entirely right.
 */

interface ControlProps {
  question: Question;
  answer: VisitorAnswer | undefined;
  onChange: (answer: VisitorAnswer) => void;
}

/* --------------------------------------------------------------------------
   Single choice — also used for `scenario`, which differs only in presentation.
   -------------------------------------------------------------------------- */

function SingleChoice({ question, answer, onChange }: ControlProps) {
  const selectedId = answer?.kind === 'single' ? answer.optionId : null;

  return (
    <div className={styles.options}>
      {(question.options ?? []).map((option) => {
        const selected = option.id === selectedId;
        return (
          <label
            key={option.id}
            className={`${styles.option} ${selected ? styles.optionSelected : ''}`}
          >
            <input
              className={styles.optionInput}
              type="radio"
              name={question.id}
              value={option.id}
              checked={selected}
              onChange={() =>
                onChange({ questionId: question.id, kind: 'single', optionId: option.id })
              }
            />
            <Indicator shape="circle" />
            <span className={styles.optionText}>
              <span className={styles.optionLabel}>{option.label}</span>
              {option.detail ? (
                <span className={styles.optionDetail}>{option.detail}</span>
              ) : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Multiple choice
   -------------------------------------------------------------------------- */

function MultiChoice({ question, answer, onChange }: ControlProps) {
  const selected = answer?.kind === 'multi' ? answer.optionIds : [];
  const max = question.maxSelections;
  const atLimit = max !== undefined && selected.length >= max;

  const toggle = (optionId: string) => {
    const next = selected.includes(optionId)
      ? selected.filter((id) => id !== optionId)
      : [...selected, optionId];
    onChange({ questionId: question.id, kind: 'multi', optionIds: next });
  };

  return (
    <>
      <div className={styles.options}>
        {(question.options ?? []).map((option) => {
          const isSelected = selected.includes(option.id);
          const disabled = atLimit && !isSelected;

          return (
            <label
              key={option.id}
              className={[
                styles.option,
                isSelected ? styles.optionSelected : '',
                disabled ? styles.optionDisabled : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <input
                className={styles.optionInput}
                type="checkbox"
                name={`${question.id}-${option.id}`}
                value={option.id}
                checked={isSelected}
                disabled={disabled}
                onChange={() => toggle(option.id)}
              />
              <Indicator shape="square" />
              <span className={styles.optionText}>
                <span className={styles.optionLabel}>{option.label}</span>
                {option.detail ? (
                  <span className={styles.optionDetail}>{option.detail}</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>

      <p className={styles.selectionHint} aria-live="polite">
        {max === undefined
          ? `${selected.length} selected`
          : `${selected.length} of up to ${max} selected`}
      </p>
    </>
  );
}

/* --------------------------------------------------------------------------
   Scale

   Five tappable targets rather than an <input type="range">. A range slider is
   fiddly with a thumb on a phone and awkward to announce; five radios are big,
   obvious, and describe themselves.
   -------------------------------------------------------------------------- */

function ScaleChoice({ question, answer, onChange }: ControlProps) {
  const scale = question.scale;
  if (!scale) return null;

  const selectedValue = answer?.kind === 'scale' ? answer.value : null;
  const values = Array.from(
    { length: scale.max - scale.min + 1 },
    (_, index) => scale.min + index,
  );

  const describe = (value: number): string =>
    scale.valueLabels?.[value] ?? `${value} of ${scale.max}`;

  return (
    <div className={styles.scale}>
      <div
        className={styles.scaleTrack}
        style={{ '--scale-steps': values.length } as CSSProperties}
      >
        {values.map((value) => {
          const selected = value === selectedValue;
          // Dots grow across the track so the direction of the scale is
          // legible at a glance, without needing to read the end labels.
          const dotSize = 8 + (value - scale.min) * 3;

          return (
            <label
              key={value}
              className={`${styles.scaleOption} ${selected ? styles.scaleOptionSelected : ''}`}
            >
              <input
                className={styles.optionInput}
                type="radio"
                name={question.id}
                value={value}
                checked={selected}
                onChange={() => onChange({ questionId: question.id, kind: 'scale', value })}
                aria-label={describe(value)}
              />
              <span
                className={styles.scaleDot}
                style={{ width: dotSize, height: dotSize }}
                aria-hidden="true"
              />
              <span className={styles.scaleValue} aria-hidden="true">
                {value}
              </span>
            </label>
          );
        })}
      </div>

      <div className={styles.scaleEnds} aria-hidden="true">
        <span>{scale.minLabel}</span>
        <span>{scale.maxLabel}</span>
      </div>

      <p className={styles.scaleChosen} aria-live="polite">
        {selectedValue === null ? '' : describe(selectedValue)}
      </p>
    </div>
  );
}

/* --------------------------------------------------------------------------
   The switch
   -------------------------------------------------------------------------- */

export function AnswerControls(props: ControlProps) {
  const { question } = props;

  return (
    <fieldset className={styles.fieldset}>
      <legend className={styles.legend}>{question.prompt}</legend>
      {question.kind === 'multi' ? (
        <MultiChoice {...props} />
      ) : question.kind === 'scale' ? (
        <ScaleChoice {...props} />
      ) : (
        <SingleChoice {...props} />
      )}
    </fieldset>
  );
}

/** True when an answer counts as "given" and the journey may advance. */
export function isAnswered(answer: VisitorAnswer | undefined): boolean {
  if (!answer) return false;
  if (answer.kind === 'multi') return answer.optionIds.length > 0;
  return true;
}
